import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Calculator, Download, TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  LineChart as ReLineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { api, apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency, fmtPct } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const HORIZONS = [5, 10, 20];

const verdictMeta = (t) => ({
  underpriced: { icon: TrendingUp, color: "hsl(var(--success))", label: t("verdict_underpriced") },
  overpriced: { icon: TrendingDown, color: "hsl(var(--destructive))", label: t("verdict_overpriced") },
  on_market: { icon: Minus, color: "hsl(var(--warning))", label: t("verdict_on_market") },
});

export default function DealAnalyzer() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    purchase_price: 200000, area: 80, region: "Lisboa", monthly_rent: 900, monthly_costs: 80,
    annual_taxes: 300, loan_amount: 150000, interest_rate: 3.5, loan_years: 30,
  });
  const [advanced, setAdvanced] = useState(false);
  const [result, setResult] = useState(null);
  const [horizon, setHorizon] = useState(10);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { data: regions = [] } = useQuery({
    queryKey: ["market-regions"], queryFn: async () => (await api.get("/market/regions")).data.regions,
  });

  const buildPayload = () => ({
    purchase_price: parseFloat(form.purchase_price) || 0, area: parseFloat(form.area) || 0,
    region: form.region || "Outra", monthly_rent: parseFloat(form.monthly_rent) || 0,
    monthly_costs: parseFloat(form.monthly_costs) || 0, annual_taxes: parseFloat(form.annual_taxes) || 0,
    loan_amount: parseFloat(form.loan_amount) || 0, interest_rate: parseFloat(form.interest_rate) || 0,
    loan_years: parseInt(form.loan_years) || 30,
    inflation_pct: form.inflation_pct !== undefined ? parseFloat(form.inflation_pct) : 2.0,
    maintenance_pct_of_value: form.maintenance_pct_of_value !== undefined ? parseFloat(form.maintenance_pct_of_value) : 1.0,
    appreciation_pct: form.appreciation_pct !== undefined ? parseFloat(form.appreciation_pct) : 0.0,
  });

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post("/deal-analyzer/analyze", buildPayload());
      setResult(res.data);
    } catch (e) {
      toast.error(apiErrorMessage(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.post("/deal-analyzer/report-pdf", buildPayload(), { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "deal-analyzer-report.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErrorMessage(e?.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const h = result?.metrics?.horizons?.[horizon];

  return (
    <div className="space-y-6" data-testid="deal-analyzer-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-sm bg-secondary flex items-center justify-center">
          <Calculator className="h-5 w-5 text-[hsl(var(--success))]" />
        </div>
        <div>
          <h1 className="font-display text-4xl">{t("deal_analyzer_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("deal_analyzer_sub")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 rounded-sm border-border space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>{t("purchase_price")}</Label>
              <Input type="number" data-testid="da-price" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="rounded-sm font-mono" /></div>
            <div className="space-y-2"><Label>{t("area")}</Label>
              <Input type="number" data-testid="da-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="rounded-sm font-mono" /></div>
            <div className="col-span-2 space-y-2"><Label>{t("region")}</Label>
              <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
                <SelectTrigger data-testid="da-region" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>{t("monthly_rent")}</Label>
              <Input type="number" data-testid="da-rent" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} className="rounded-sm font-mono" /></div>
            <div className="space-y-2"><Label>{t("monthly_costs")}</Label>
              <Input type="number" data-testid="da-costs" value={form.monthly_costs} onChange={(e) => setForm({ ...form, monthly_costs: e.target.value })} className="rounded-sm font-mono" /></div>
            <div className="space-y-2"><Label>{t("annual_taxes")}</Label>
              <Input type="number" data-testid="da-taxes" value={form.annual_taxes} onChange={(e) => setForm({ ...form, annual_taxes: e.target.value })} className="rounded-sm font-mono" /></div>
            <div className="space-y-2"><Label>{t("loan_amount")}</Label>
              <Input type="number" data-testid="da-loan" value={form.loan_amount} onChange={(e) => setForm({ ...form, loan_amount: e.target.value })} className="rounded-sm font-mono" /></div>
            <div className="space-y-2"><Label>{t("interest_rate")}</Label>
              <Input type="number" data-testid="da-rate" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} className="rounded-sm font-mono" /></div>
            <div className="space-y-2"><Label>{t("loan_years")}</Label>
              <Input type="number" data-testid="da-years" value={form.loan_years} onChange={(e) => setForm({ ...form, loan_years: e.target.value })} className="rounded-sm font-mono" /></div>
          </div>

          <button type="button" onClick={() => setAdvanced(!advanced)} className="text-xs text-muted-foreground hover:text-foreground underline">
            {advanced ? t("hide_assumptions") : t("show_assumptions")}
          </button>
          {advanced && (
            <div className="grid grid-cols-3 gap-4 pt-2 border-t border-border">
              <div className="space-y-2"><Label>{t("inflation_pct")}</Label>
                <Input type="number" data-testid="da-inflation" value={form.inflation_pct ?? 2.0} onChange={(e) => setForm({ ...form, inflation_pct: e.target.value })} className="rounded-sm font-mono" /></div>
              <div className="space-y-2"><Label>{t("maintenance_pct")}</Label>
                <Input type="number" data-testid="da-maintenance" value={form.maintenance_pct_of_value ?? 1.0} onChange={(e) => setForm({ ...form, maintenance_pct_of_value: e.target.value })} className="rounded-sm font-mono" /></div>
              <div className="space-y-2"><Label>{t("appreciation_pct")}</Label>
                <Input type="number" data-testid="da-appreciation" value={form.appreciation_pct ?? 0.0} onChange={(e) => setForm({ ...form, appreciation_pct: e.target.value })} className="rounded-sm font-mono" /></div>
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={run} disabled={loading} data-testid="da-run" className="flex-1 rounded-sm">{loading ? "…" : t("analyze")}</Button>
            <Button onClick={exportPdf} disabled={!result || exporting} data-testid="da-export" variant="outline" className="rounded-sm gap-2">
              <Download className="h-4 w-4" /> {t("export_pdf")}
            </Button>
          </div>
        </Card>

        <div>
          {!result ? (
            <Card className="p-16 rounded-sm border-dashed border-border h-full flex flex-col items-center justify-center text-center">
              <Calculator className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-muted-foreground">{t("analyze")}</p>
            </Card>
          ) : (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="da-result">
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-5 rounded-sm border-border">
                  <span className="text-xs uppercase text-muted-foreground">{t("gross_yield")}</span>
                  <p className="font-display text-2xl tabular-nums mt-1">{fmtPct(result.metrics.gross_yield)}</p>
                </Card>
                <Card className="p-5 rounded-sm border-border">
                  <span className="text-xs uppercase text-muted-foreground">{t("net_yield")}</span>
                  <p className="font-display text-2xl tabular-nums mt-1">{fmtPct(result.metrics.net_yield)}</p>
                </Card>
                <Card className="p-5 rounded-sm border-border">
                  <span className="text-xs uppercase text-muted-foreground">{t("monthly_cashflow")}</span>
                  <p className={`font-display text-2xl tabular-nums mt-1 ${result.metrics.monthly_cashflow < 0 ? "text-[hsl(var(--destructive))]" : ""}`}>
                    {fmtCurrency(result.metrics.monthly_cashflow)}
                  </p>
                </Card>
                <Card className="p-5 rounded-sm border-border">
                  <span className="text-xs uppercase text-muted-foreground">{t("vacancy_risk")}</span>
                  <p className="font-display text-2xl tabular-nums mt-1">{fmtPct(result.metrics.vacancy_risk_pct)}</p>
                </Card>
              </div>

              {(() => {
                const v = (verdictMeta(t))[result.market_comparison.verdict] || verdictMeta(t).on_market;
                const V = v.icon;
                return (
                  <Card className="p-4 rounded-sm border-border" style={{ borderColor: v.color }}>
                    <div className="flex items-center gap-3">
                      <V className="h-5 w-5" style={{ color: v.color }} />
                      <span className="font-display text-lg">{v.label}</span>
                      <span className="text-xs text-muted-foreground ml-auto" data-testid="da-data-source">
                        {result.market_comparison.data_source === "live" ? t("data_source_live") : t("data_source_static")}
                        {" · "}{t("vs_market")}: {fmtCurrency(result.market_comparison.reference.rent_per_m2)}/m²
                      </span>
                    </div>
                  </Card>
                );
              })()}

              <Card className="p-6 rounded-sm border-border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("cashflow_projection")}</h3>
                  <div className="flex gap-1">
                    {HORIZONS.map((y) => (
                      <button key={y} data-testid={`da-horizon-${y}`} onClick={() => setHorizon(y)}
                        className={`px-3 py-1 text-xs rounded-sm ${horizon === y ? "bg-secondary text-foreground font-medium" : "text-muted-foreground hover:bg-secondary/60"}`}>
                        {y}a
                      </button>
                    ))}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <ReLineChart data={h?.cashflows || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} width={50} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }}
                      formatter={(v) => fmtCurrency(v)}
                    />
                    <Line type="monotone" dataKey="cumulative_cashflow" stroke="#10B981" strokeWidth={2} dot={false} />
                  </ReLineChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-border">
                  <div><span className="text-xs text-muted-foreground">{t("roi")}</span>
                    <p className="font-display text-lg tabular-nums">{h?.roi_pct !== null && h?.roi_pct !== undefined ? fmtPct(h.roi_pct) : "—"}</p></div>
                  <div><span className="text-xs text-muted-foreground">{t("irr")}</span>
                    <p className="font-display text-lg tabular-nums">{h?.irr_pct !== null && h?.irr_pct !== undefined ? fmtPct(h.irr_pct) : "n/d"}</p></div>
                  <div><span className="text-xs text-muted-foreground">{t("break_even")}</span>
                    <p className="font-display text-lg tabular-nums">{h?.break_even_year ? `${t("year_short")} ${h.break_even_year}` : "—"}</p></div>
                  <div><span className="text-xs text-muted-foreground">{t("total_gain")}</span>
                    <p className="font-display text-lg tabular-nums">{fmtCurrency(h?.total_gain || 0)}</p></div>
                </div>
              </Card>

              <Card className="rounded-sm border-border overflow-hidden" data-testid="da-stress-test">
                <div className="px-6 py-4 border-b border-border">
                  <h3 className="text-sm uppercase tracking-wider text-muted-foreground">{t("stress_test")}</h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("scenario")}</TableHead>
                      <TableHead className="text-right">{t("monthly_cashflow")}</TableHead>
                      <TableHead className="text-right">{t("roi")} (10a)</TableHead>
                      <TableHead className="text-right">{t("break_even")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.stress_test.map((s) => (
                      <TableRow key={s.key}>
                        <TableCell className={s.key === "base" ? "font-medium" : ""}>{s.label}</TableCell>
                        <TableCell className={`text-right tabular-nums ${s.monthly_cashflow < 0 ? "text-[hsl(var(--destructive))]" : ""}`}>{fmtCurrency(s.monthly_cashflow)}</TableCell>
                        <TableCell className="text-right tabular-nums">{s.roi_10y_pct !== null ? fmtPct(s.roi_10y_pct) : "—"}</TableCell>
                        <TableCell className="text-right tabular-nums">{s.break_even_year ? `${t("year_short")} ${s.break_even_year}` : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
